import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { aboutContent, partners, siteConfig } from '../../data/mock';
import {
  Target,
  Users,
  Award,
  CheckCircle2,
  ArrowRight,
  MessageCircle,
  Briefcase,
  TrendingUp,
  Shield,
  Clock,
} from 'lucide-react';

export const AboutPage = () => {
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(siteConfig.whatsappMessage)}`,
      '_blank'
    );
  };

  const values = [
    {
      icon: Target,
      title: 'Foco no Resultado',
      description: 'Cada projeto é guiado por objetivos claros e métricas de sucesso.',
    },
    {
      icon: Users,
      title: 'Parceria Verdadeira',
      description: 'Trabalhamos lado a lado com nossos clientes como um time só.',
    },
    {
      icon: Award,
      title: 'Excelência Técnica',
      description: 'Equipe certificada e atualizada nas melhores práticas do mercado.',
    },
    {
      icon: Shield,
      title: 'Compromisso Ético',
      description: 'Transparência e integridade em todas as nossas relações.',
    },
  ];

  const methodology = [
    {
      step: '01',
      title: 'Diagnóstico',
      description: 'Análise completa do seu ambiente de TI e necessidades do negócio.',
    },
    {
      step: '02',
      title: 'Planejamento',
      description: 'Desenvolvimento de um plano de ação detalhado com cronogramas claros.',
    },
    {
      step: '03',
      title: 'Execução',
      description: 'Implementação das soluções com entregas contínuas e comunicação proativa.',
    },
    {
      step: '04',
      title: 'Suporte Contínuo',
      description: 'Acompanhamento e otimização constante para garantir resultados.',
    },
  ];

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#329780]/10 border border-[#329780]/20">
                <Briefcase className="w-4 h-4 text-[#329780] mr-2" />
                <span className="text-[#329780] text-sm font-medium">
                  Sobre a KC Byte iT
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
                Transformando <span className="text-[#329780]">negócios</span> através da tecnologia
              </h1>

              <p className="text-lg text-slate-400 leading-relaxed">
                Somos uma empresa especializada em soluções de TI, comprometida em
                entregar excelência e inovação para empresas que buscam se destacar no
                mercado digital.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contato">
                  <Button
                    size="lg"
                    className="bg-[#329780] hover:bg-[#287a68] text-white px-8 h-12"
                  >
                    Fale Conosco
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleWhatsApp}
                  className="border-slate-700 text-slate-300 hover:bg-white/5 px-8 h-12"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {aboutContent.stats.map((stat, index) => (
                <Card
                  key={index}
                  className="bg-[#111920] border-[#1e293b] card-hover"
                >
                  <CardContent className="p-6 text-center">
                    <p className="text-4xl font-bold text-[#329780] mb-2">
                      {stat.value}
                    </p>
                    <p className="text-slate-400 text-sm">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#080c10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-[#329780]/10 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#329780]" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                {aboutContent.mission.title}
              </h2>
              <p className="text-slate-400 leading-relaxed text-lg">
                {aboutContent.mission.content}
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#329780]/5 rounded-3xl" />
              <div className="relative bg-[#111920] border border-[#1e293b] rounded-2xl p-8 space-y-6">
                <h3 className="text-xl font-semibold text-white">Nossos Valores</h3>
                {values.map((value, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-lg bg-[#329780]/10 flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-5 h-5 text-[#329780]" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{value.title}</h4>
                      <p className="text-slate-400 text-sm">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="w-16 h-16 rounded-2xl bg-[#329780]/10 flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-8 h-8 text-[#329780]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {aboutContent.methodology.title}
            </h2>
            <p className="text-slate-400 max-w-3xl mx-auto leading-relaxed">
              {aboutContent.methodology.content.split('\n\n')[0]}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodology.map((item, index) => (
              <Card
                key={index}
                className="bg-[#111920] border-[#1e293b] card-hover relative overflow-hidden group"
              >
                <CardContent className="p-6 space-y-4">
                  <span className="text-5xl font-bold text-[#329780]/10 group-hover:text-[#329780]/20 transition-colors duration-300">
                    {item.step}
                  </span>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-[#080c10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-[#111920] border border-[#1e293b] rounded-2xl p-8 space-y-6">
                <h3 className="text-xl font-semibold text-white mb-6">
                  O que você ganha com a KC Byte iT
                </h3>
                {[
                  'Equipe certificada e experiente',
                  'Comunicação transparente e proativa',
                  'Entregas dentro do prazo acordado',
                  'Suporte contínuo pós-projeto',
                  'Metodologias ágeis e eficientes',
                  'Foco total em resultados',
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#329780] flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="w-16 h-16 rounded-2xl bg-[#329780]/10 flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-[#329780]" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                {aboutContent.trust.title}
              </h2>
              <div className="text-slate-400 leading-relaxed space-y-4">
                {aboutContent.trust.content.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Tecnologias e <span className="text-[#329780]">Parceiros</span>
            </h2>
            <p className="text-slate-400">
              Trabalhamos com as melhores soluções do mercado
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="px-8 py-4 rounded-xl bg-[#111920] border border-[#1e293b] hover:border-[#329780]/30 transition-colors duration-300"
              >
                <span className="text-slate-400 font-medium">{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#080c10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-[#111920] border border-[#1e293b] rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#329780]/10 rounded-full blur-3xl" />
            <div className="relative space-y-6">
              <Clock className="w-12 h-12 text-[#329780] mx-auto" />
              <h2 className="text-3xl font-bold text-white">
                Vamos conversar sobre seu projeto?
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Agende uma consulta gratuita e descubra como podemos ajudar sua empresa.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link to="/contato">
                  <Button
                    size="lg"
                    className="bg-[#329780] hover:bg-[#287a68] text-white px-8 h-12"
                  >
                    Agendar Consulta
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
